/**
 * 自定义边组件 - 带删除按钮和执行状态高亮
 *
 * 基于 React Flow BaseEdge 实现自定义边样式和交互
 * Features:
 * - 悬停时显示删除按钮
 * - 支持边标签显示
 * - 支持执行状态样式和动画
 */

import { useCallback, memo, useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  type Edge,
  useReactFlow,
} from '@xyflow/react';
import { NodeExecutionStatus } from '../../../types/workflow';
import { useExecutionStore } from '../../../store/workflowStore';
import { cn } from '../nodes/nodeStyles';
import { EDGE_STATUS_STYLES } from '../styles/nodeStatusStyles';

/**
 * 边数据类型
 */
interface ButtonEdgeData extends Record<string, unknown> {
  /** 边标签（用于条件分支显示） */
  label?: string;
  /** 条件分支 ID */
  branch_id?: string;
  /** 执行状态 */
  executionStatus?: NodeExecutionStatus;
}

/**
 * 自定义边类型
 */
type ButtonEdgeType = Edge<ButtonEdgeData>;

/**
 * ButtonEdge 组件属性
 */
type ButtonEdgeProps = EdgeProps<ButtonEdgeType>;

/**
 * 获取边的执行状态
 * 
 * 边的执行状态取决于源节点和目标节点的执行状态
 */
function getEdgeExecutionStatus(
  sourceStatus: NodeExecutionStatus | undefined,
  targetStatus: NodeExecutionStatus | undefined
): NodeExecutionStatus {
  // 如果目标节点已完成，边显示为完成
  if (targetStatus === NodeExecutionStatus.COMPLETED) {
    return NodeExecutionStatus.COMPLETED;
  }
  
  // 如果目标节点失败，边显示为失败
  if (targetStatus === NodeExecutionStatus.FAILED) {
    return NodeExecutionStatus.FAILED;
  }
  
  // 如果目标节点正在运行，边显示为运行中（表示数据流动）
  if (targetStatus === NodeExecutionStatus.RUNNING) {
    return NodeExecutionStatus.RUNNING;
  }
  
  // 如果源节点已完成但目标节点未开始，边显示为等待
  if (sourceStatus === NodeExecutionStatus.COMPLETED && 
      (targetStatus === NodeExecutionStatus.PENDING || !targetStatus)) {
    return NodeExecutionStatus.PENDING;
  }
  
  // 如果源节点正在运行，边显示为运行中
  if (sourceStatus === NodeExecutionStatus.RUNNING) {
    return NodeExecutionStatus.RUNNING;
  }
  
  // 默认为等待状态
  return NodeExecutionStatus.PENDING;
}

/**
 * 自定义边组件
 */
const ButtonEdge = memo(({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: ButtonEdgeProps) => {
  const { setEdges } = useReactFlow();
  
  // 从执行 store 获取节点状态
  const nodeLogs = useExecutionStore((state) => state.nodeLogs);
  const sourceStatus = nodeLogs.get(source)?.status;
  const targetStatus = nodeLogs.get(target)?.status;
  
  // 计算边的执行状态
  const edgeExecutionStatus = useMemo(() => {
    // 如果边有明确的执行状态，使用它
    if (data?.executionStatus) {
      return data.executionStatus;
    }
    // 否则根据节点状态计算
    return getEdgeExecutionStatus(sourceStatus, targetStatus);
  }, [data?.executionStatus, sourceStatus, targetStatus]);

  // 计算贝塞尔曲线路径
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // 删除边
  const onEdgeClick = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      setEdges((edges) => edges.filter((e) => e.id !== id));
    },
    [id, setEdges]
  );

  // 获取边样式配置
  const statusStyle = EDGE_STATUS_STYLES[edgeExecutionStatus] || EDGE_STATUS_STYLES[NodeExecutionStatus.PENDING];

  // 边样式
  const edgeStyle: React.CSSProperties = {
    ...(typeof style === 'object' ? style : {}),
    stroke: statusStyle.stroke,
    strokeWidth: selected ? 3 : statusStyle.strokeWidth,
  };

  // 是否显示动画（运行中状态）
  const showAnimation = edgeExecutionStatus === NodeExecutionStatus.RUNNING;

  return (
    <>
      {/* 动画边（运行中状态显示虚线动画） */}
      {showAnimation && (
        <path
          d={edgePath}
          fill="none"
          stroke={statusStyle.stroke}
          strokeWidth={statusStyle.strokeWidth}
          strokeDasharray="5 5"
          className="animate-edge-flow"
          style={{
            animation: 'edge-flow 0.5s linear infinite',
          }}
        />
      )}
      
      {/* 边路径 */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd ?? undefined}
        style={edgeStyle}
        className={cn(
          'transition-all duration-150',
          selected && 'react-flow__edge-selected',
        )}
      />

      {/* 边标签渲染器 */}
      <EdgeLabelRenderer>
        {/* 条件分支标签 */}
        {data?.label && (
          <div
            className="absolute pointer-events-auto transform -translate-x-1/2 -translate-y-1/2"
            style={{
              position: 'absolute',
              left: labelX,
              top: labelY - 10,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className={cn(
              'px-2 py-0.5 text-xs font-medium border rounded shadow-sm',
              edgeExecutionStatus === NodeExecutionStatus.COMPLETED
                ? 'bg-success-50 border-success-200 text-success-700'
                : edgeExecutionStatus === NodeExecutionStatus.FAILED
                ? 'bg-error-50 border-error-200 text-error-700'
                : edgeExecutionStatus === NodeExecutionStatus.RUNNING
                ? 'bg-paper-50 border-paper-200 text-paper-800'
                : 'bg-white border-gray-200 text-gray-700',
            )}>
              {data.label}
            </div>
          </div>
        )}

        {/* 删除按钮 */}
        <div
          className="absolute pointer-events-auto"
          style={{
            position: 'absolute',
            left: labelX,
            top: labelY,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <button
            type="button"
            onClick={onEdgeClick}
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center',
              'transition-all duration-150',
              selected
                ? 'opacity-100 bg-red-500 hover:bg-red-600 text-white shadow-md'
                : 'opacity-0 hover:opacity-100 bg-gray-100 hover:bg-red-500 text-gray-500 hover:text-white border border-gray-300',
            )}
            style={{
              fontSize: '12px',
            }}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

ButtonEdge.displayName = 'ButtonEdge';

export { ButtonEdge, getEdgeExecutionStatus };
export type { ButtonEdgeData, ButtonEdgeType, ButtonEdgeProps };
export default ButtonEdge;
